import * as React from 'react';
import { BodyText, ButtonAsWrapper } from '@minecraft.atoms';
import Icon, { IconProps } from '@minecraft.icon';
import { BaseDropdownButton } from './BaseDropdownButton';
import { DropdownItemProps, OptionalProps } from './types';

type MeatballButtonProps = {
  /** label used for screen readers when encountering the button */
  label: string;
  /** optional name of an icon to use for the toggle instead of the meatball icon
   * defaults to the meatball icon if not provided
   * you should only need to use this prop in very rare cases
   * as the meatball icon is the standard for this type of dropdown toggle
   */
  iconName?: IconProps['name'];
  /** size of the meatball icon */
  iconSize?: IconProps['size'];
  /** pass a ref down to the underlying button element
   * this is used so that the DropdownToggle
   * correctly opens & positions the dropdown menu
   * do not rename this prop!!
   */
  innerRef?: React.Ref<HTMLButtonElement>;
  /** this is passed through from the implementation in BaseDropdownButton
   * you should not need to use this prop manually, but we do need to declare it here
   * so that we can intercept the onClick event and stop it propagating
   * (otherwise clicking the meatball toggle will also trigger any onClick in parent element(s))
   */
  onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  /** whether the menu toggle is disabled */
  disabled?: boolean;
};

const MeatballButton = ({
  label,
  iconName = 'menu-meatball',
  iconSize = 'md',
  innerRef,
  onClick,
  ...props
}: MeatballButtonProps) => {
  return (
    <ButtonAsWrapper
      aria-label={label}
      ref={innerRef}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick?.(e);
      }}
      {...props}
    >
      <BodyText color="default">
        <Icon name={iconName} size={iconSize} inheritColor />
      </BodyText>
    </ButtonAsWrapper>
  );
};

export interface MeatballDropdownButtonProps extends OptionalProps {
  block?: boolean;
  menuAlign?: 'right' | 'left';
  /** use "body" to have the menu attach to the document body instead of being set inside the dropdown
   * this can help with overflow issues inside containers with smaller sizes where the menu might be cut off
   * in most cases you won't need this though
   */
  menuContainer?: 'body' | undefined;
  menuItemsProps: DropdownItemProps[];
  mobileBlock?: boolean;
  toggleProps: Omit<MeatballButtonProps, 'onClick'>;
}

export const MeatballDropdownButton = ({
  toggleProps,
  menuAlign = 'right',
  menuItemsProps = [],
  block = false,
  mobileBlock = false,
  'data-testid': dataTestId,
  qaId,
  className,
  menuContainer,
}: MeatballDropdownButtonProps) => (
  <BaseDropdownButton
    block={block}
    className={className}
    data-testid={dataTestId}
    menuAlign={menuAlign}
    menuContainer={menuContainer}
    menuItemsProps={menuItemsProps}
    mobileBlock={mobileBlock}
    qaId={qaId}
    toggleChildren={undefined}
    ToggleComponent={MeatballButton}
    toggleProps={{ ...toggleProps }}
  />
);
